'use client';

import React, { useState, useRef, useEffect } from 'react';

/* ═══════════════════════════════════════════════════════════════
   Types
   ═══════════════════════════════════════════════════════════════ */

export type ComparisonMode = 'full' | 'roi' | 'compact' | 'features';

export interface CompetitorPricing {
  entry?: string;
  mid?: string;
  enterprise?: string;
  perMinute?: string;
}

export interface CompetitorData {
  name: string;
  type: string;
  realTime: boolean | null;
  sdk: string;
  pricing: CompetitorPricing;
  lipSync: boolean;
  latency: string;
  tier: 1 | 2;
  features: {
    realTimeStreaming: boolean | null;
    twoDSupport: boolean;
    customCharacters: string;
    developerSDK: boolean;
    brandMascots: boolean;
    offlineCapable: boolean | null;
    emotionalAI: boolean | null;
    multiLanguage: string;
    frameRate: string;
  };
  isHero?: boolean;
}

export interface CompetitorComparisonProps {
  mode?: ComparisonMode;
  data?: CompetitorData[];
  asOf?: string;
  showModeSwitcher?: boolean;
  className?: string;
}

/* ═══════════════════════════════════════════════════════════════
   Default competitor data — sourced from competitor research
   Pricing verified as of May 2026. Mascotbot perMinute = usage-based Starter
   rate; production plans (Launch/Growth/Scale) bill flat per monthly active user.
   ═══════════════════════════════════════════════════════════════ */

const DEFAULT_DATA: CompetitorData[] = [
  {
    name: 'Mascotbot',
    type: '2D Rive',
    realTime: true,
    sdk: 'React SDK',
    pricing: { entry: '$49/mo', mid: '$99/mo', enterprise: '$999/mo', perMinute: '$0.04' },
    lipSync: true,
    latency: '<10ms',
    tier: 1,
    features: {
      realTimeStreaming: true, twoDSupport: true, customCharacters: 'Rive files',
      developerSDK: true, brandMascots: true, offlineCapable: true,
      emotionalAI: null, multiLanguage: 'Via TTS provider', frameRate: '120 fps',
    },
    isHero: true,
  },
  {
    name: 'HeyGen LiveAvatar',
    type: 'Photo-realistic',
    realTime: true,
    sdk: 'JS / REST API',
    pricing: { entry: '$24/mo', mid: '$99/mo', enterprise: 'From $25k', perMinute: '$0.10–0.20' },
    lipSync: true,
    latency: 'Low (WebRTC)',
    tier: 1,
    features: {
      realTimeStreaming: true, twoDSupport: false, customCharacters: 'Video recording',
      developerSDK: true, brandMascots: false, offlineCapable: false,
      emotionalAI: true, multiLanguage: '40+ languages', frameRate: '30 fps',
    },
  },
  {
    name: 'D-ID',
    type: 'Photo-realistic',
    realTime: true,
    sdk: 'REST / Streaming',
    pricing: { entry: '$5.90/mo', mid: '$29.90/mo', enterprise: 'Custom', perMinute: '~$0.15' },
    lipSync: true,
    latency: '100 FPS render',
    tier: 1,
    features: {
      realTimeStreaming: true, twoDSupport: false, customCharacters: 'Single photo',
      developerSDK: true, brandMascots: false, offlineCapable: false,
      emotionalAI: false, multiLanguage: '100+ languages', frameRate: '30 fps',
    },
  },
  {
    name: 'Beyond Presence',
    type: 'Photo-realistic',
    realTime: true,
    sdk: 'API',
    pricing: { entry: 'Contact sales', enterprise: 'Custom', perMinute: 'Custom' },
    lipSync: true,
    latency: '<100ms',
    tier: 1,
    features: {
      realTimeStreaming: true, twoDSupport: false, customCharacters: 'Studio recording',
      developerSDK: true, brandMascots: false, offlineCapable: false,
      emotionalAI: false, multiLanguage: 'Multiple', frameRate: '30 fps',
    },
  },
  {
    name: 'Tavus',
    type: 'Photo-realistic',
    realTime: true,
    sdk: 'API',
    pricing: { enterprise: 'Custom', perMinute: 'Custom' },
    lipSync: true,
    latency: 'Low',
    tier: 1,
    features: {
      realTimeStreaming: true, twoDSupport: false, customCharacters: '2-min video',
      developerSDK: true, brandMascots: false, offlineCapable: false,
      emotionalAI: true, multiLanguage: '30+ languages', frameRate: '30 fps',
    },
  },
  {
    name: 'GliaStar',
    type: '2D Mascot',
    realTime: false,
    sdk: 'API / SDK',
    pricing: { enterprise: 'Custom', perMinute: 'Custom' },
    lipSync: true,
    latency: 'N/A',
    tier: 1,
    features: {
      realTimeStreaming: false, twoDSupport: true, customCharacters: 'Character builder',
      developerSDK: true, brandMascots: true, offlineCapable: null,
      emotionalAI: false, multiLanguage: 'Multiple', frameRate: 'N/A',
    },
  },
  {
    name: 'Synthesia',
    type: 'Photo-realistic',
    realTime: false,
    sdk: 'REST (Enterprise)',
    pricing: { entry: '$18/mo', mid: '$59/mo', enterprise: 'Custom', perMinute: 'N/A' },
    lipSync: true,
    latency: 'Pre-rendered',
    tier: 2,
    features: {
      realTimeStreaming: false, twoDSupport: false, customCharacters: '$1k/yr custom',
      developerSDK: false, brandMascots: false, offlineCapable: false,
      emotionalAI: false, multiLanguage: '160+ languages', frameRate: '30 fps',
    },
  },
  {
    name: 'Colossyan',
    type: 'Photo-realistic',
    realTime: false,
    sdk: 'API',
    pricing: { entry: '$27/mo', enterprise: 'Custom', perMinute: 'N/A' },
    lipSync: true,
    latency: 'Pre-rendered',
    tier: 2,
    features: {
      realTimeStreaming: false, twoDSupport: false, customCharacters: 'Photo upload',
      developerSDK: false, brandMascots: false, offlineCapable: false,
      emotionalAI: false, multiLanguage: '70+ languages', frameRate: '30 fps',
    },
  },
  {
    name: 'AKOOL',
    type: 'Photo-realistic',
    realTime: false,
    sdk: 'REST',
    pricing: { entry: '~$30/mo', enterprise: 'Custom', perMinute: 'N/A' },
    lipSync: true,
    latency: 'Pre-rendered',
    tier: 2,
    features: {
      realTimeStreaming: false, twoDSupport: false, customCharacters: 'Face swap',
      developerSDK: false, brandMascots: false, offlineCapable: false,
      emotionalAI: false, multiLanguage: 'Multiple', frameRate: '30 fps',
    },
  },
  {
    name: 'Elai.io',
    type: '2D Cartoon',
    realTime: false,
    sdk: 'None',
    pricing: { enterprise: 'Custom', perMinute: 'N/A' },
    lipSync: true,
    latency: 'Pre-rendered',
    tier: 2,
    features: {
      realTimeStreaming: false, twoDSupport: true, customCharacters: 'Image upload',
      developerSDK: false, brandMascots: true, offlineCapable: false,
      emotionalAI: false, multiLanguage: 'Multiple', frameRate: 'N/A',
    },
  },
];

const DEFAULT_AS_OF = 'May 2026';

/* ═══════════════════════════════════════════════════════════════
   Helpers
   ═══════════════════════════════════════════════════════════════ */

function Bool({ value }: { value: boolean | null }) {
  if (value === null) return <span className="cc-bool cc-bool--unknown">—</span>;
  return value
    ? <span className="cc-bool cc-bool--yes" aria-label="Yes">&#10003;</span>
    : <span className="cc-bool cc-bool--no" aria-label="No">—</span>;
}

function Price({ pricing }: { pricing: CompetitorPricing }) {
  if (pricing.entry) return <>{pricing.entry}</>;
  if (pricing.enterprise === 'Custom') return <span className="cc-custom">Custom</span>;
  return <span className="cc-custom">—</span>;
}

/* ═══════════════════════════════════════════════════════════════
   Tables — one per mode, unified for all viewports
   ═══════════════════════════════════════════════════════════════ */

function FullTable({ data }: { data: CompetitorData[] }) {
  return (
    <table className="cc-table">
      <thead>
        <tr>
          <th className="cc-sticky">Platform</th>
          <th>Cost/min</th>
          <th>From</th>
          <th>Type</th>
          <th>Real-time</th>
          <th>Latency</th>
          <th>SDK</th>
          <th>Lip Sync</th>
        </tr>
      </thead>
      <tbody>
        {data.map((c) => (
          <tr key={c.name} className={c.isHero ? 'cc-hero' : ''}>
            <td className="cc-name cc-sticky">
              {c.isHero && <span className="cc-dot" />}
              {c.name}
            </td>
            <td className={`cc-num${c.isHero ? ' cc-highlight' : ''}`}>
              {c.pricing.perMinute || '—'}
            </td>
            <td className="cc-num"><Price pricing={c.pricing} /></td>
            <td><span className="cc-pill">{c.type}</span></td>
            <td className="cc-center"><Bool value={c.realTime} /></td>
            <td className="cc-small">{c.latency}</td>
            <td className="cc-small">{c.sdk}</td>
            <td className="cc-center"><Bool value={c.lipSync} /></td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function ROITable({ data }: { data: CompetitorData[] }) {
  const rows = data.filter((c) => c.isHero || c.pricing.perMinute !== 'N/A');
  return (
    <table className="cc-table">
      <thead>
        <tr>
          <th className="cc-sticky">Platform</th>
          <th>Cost/min</th>
          <th>Entry</th>
          <th>Mid-tier</th>
          <th>Enterprise</th>
          <th>Real-time</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((c) => (
          <tr key={c.name} className={c.isHero ? 'cc-hero' : ''}>
            <td className="cc-name cc-sticky">
              {c.isHero && <span className="cc-dot" />}
              {c.name}
            </td>
            <td className={`cc-num${c.isHero ? ' cc-highlight' : ''}`}>
              {c.pricing.perMinute || '—'}
            </td>
            <td className="cc-num">{c.pricing.entry || '—'}</td>
            <td className="cc-num">{c.pricing.mid || '—'}</td>
            <td className="cc-num">{c.pricing.enterprise || '—'}</td>
            <td className="cc-center"><Bool value={c.realTime} /></td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function CompactTable({ data }: { data: CompetitorData[] }) {
  const rows = data.filter((c) => c.tier === 1);
  return (
    <table className="cc-table">
      <thead>
        <tr>
          <th className="cc-sticky">Platform</th>
          <th>Cost/min</th>
          <th>From</th>
          <th>Type</th>
          <th>Real-time</th>
          <th>Latency</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((c) => (
          <tr key={c.name} className={c.isHero ? 'cc-hero' : ''}>
            <td className="cc-name cc-sticky">
              {c.isHero && <span className="cc-dot" />}
              {c.name}
            </td>
            <td className={`cc-num${c.isHero ? ' cc-highlight' : ''}`}>
              {c.pricing.perMinute || '—'}
            </td>
            <td className="cc-num"><Price pricing={c.pricing} /></td>
            <td><span className="cc-pill">{c.type}</span></td>
            <td className="cc-center"><Bool value={c.realTime} /></td>
            <td className="cc-small">{c.latency}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function FeatureTable({ data }: { data: CompetitorData[] }) {
  const rows = data.filter((c) => c.tier === 1);
  const boolFeats: { key: keyof CompetitorData['features']; label: string }[] = [
    { key: 'realTimeStreaming', label: 'Real-time streaming' },
    { key: 'twoDSupport', label: '2D avatar support' },
    { key: 'developerSDK', label: 'Developer SDK' },
    { key: 'brandMascots', label: 'Brand mascots' },
    { key: 'offlineCapable', label: 'Offline capable' },
    { key: 'emotionalAI', label: 'Emotional AI' },
  ];
  const textFeats: { key: keyof CompetitorData['features']; label: string }[] = [
    { key: 'customCharacters', label: 'Custom characters' },
    { key: 'multiLanguage', label: 'Multi-language' },
    { key: 'frameRate', label: 'Frame rate' },
  ];

  return (
    <table className="cc-table cc-table--features">
      <thead>
        <tr>
          <th className="cc-sticky cc-feat-th">Feature</th>
          {rows.map((c) => (
            <th
              key={c.name}
              className={c.isHero ? 'cc-th-hero cc-sticky-hero' : ''}
            >
              <span className={c.isHero ? 'cc-th-hero-badge' : ''}>{c.name}</span>
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {boolFeats.map((f) => (
          <tr key={f.key}>
            <td className="cc-feat-label cc-sticky">{f.label}</td>
            {rows.map((c) => {
              const v = c.features[f.key];
              return (
                <td key={c.name} className={`cc-center${c.isHero ? ' cc-hero-col cc-sticky-hero' : ''}`}>
                  <Bool value={typeof v === 'boolean' ? v : null} />
                </td>
              );
            })}
          </tr>
        ))}
        {textFeats.map((f) => (
          <tr key={f.key}>
            <td className="cc-feat-label cc-sticky">{f.label}</td>
            {rows.map((c) => (
              <td key={c.name} className={`${c.isHero ? 'cc-hero-col cc-sticky-hero' : 'cc-small'}`}>
                {String(c.features[f.key])}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/* ═══════════════════════════════════════════════════════════════
   Mode Switcher
   ═══════════════════════════════════════════════════════════════ */

const MODE_LABELS: Record<ComparisonMode, string> = {
  full: 'Overview',
  roi: 'Cost & ROI',
  compact: 'Top 6',
  features: 'Features',
};

function ModeSwitcher({
  active,
  onChange,
}: {
  active: ComparisonMode;
  onChange: (m: ComparisonMode) => void;
}) {
  return (
    <div className="cc-tabs" role="tablist" aria-label="Comparison view">
      {(Object.keys(MODE_LABELS) as ComparisonMode[]).map((m) => (
        <button
          key={m}
          role="tab"
          aria-selected={active === m}
          className={`cc-tab${active === m ? ' cc-tab--on' : ''}`}
          onClick={() => onChange(m)}
        >
          {MODE_LABELS[m]}
        </button>
      ))}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   Main
   ═══════════════════════════════════════════════════════════════ */

export function CompetitorComparison({
  mode: initialMode = 'full',
  data = DEFAULT_DATA,
  asOf = DEFAULT_AS_OF,
  showModeSwitcher = false,
  className,
}: CompetitorComparisonProps) {
  const [mode, setMode] = useState<ComparisonMode>(initialMode);
  const rootRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = rootRef.current;
    const parent = el?.parentElement;
    if (!el || !parent) return;

    const recalc = () => {
      const parentLeft = parent.getBoundingClientRect().left;
      const vw = document.documentElement.clientWidth;
      const inset = 5; // px padding from viewport edges
      el.style.marginLeft = `${-(parentLeft - inset)}px`;
      el.style.width = `${vw - inset * 2}px`;
      // Offset for aligning tabs with article body content
      el.style.setProperty('--cc-content-offset', `${parentLeft - inset}px`);
    };

    recalc();

    // Observe the parent instead of hammering 15 RAFs to catch async layout
    // shifts (TOC sidebar appearing, fonts loading, etc). ResizeObserver
    // fires exactly when geometry actually changes.
    const ro = new ResizeObserver(recalc);
    ro.observe(parent);
    ro.observe(document.documentElement);

    return () => {
      ro.disconnect();
    };
  }, []);

  /* Toggle shadow on sticky column when panel is scrolled horizontally */
  useEffect(() => {
    const panel = panelRef.current;
    if (!panel) return;
    const onScroll = () => {
      panel.classList.toggle('cc-panel--scrolled', panel.scrollLeft > 0);
    };
    panel.addEventListener('scroll', onScroll, { passive: true });
    return () => panel.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div ref={rootRef} className={`cc-root${className ? ` ${className}` : ''}`}>
      {showModeSwitcher && <ModeSwitcher active={mode} onChange={setMode} />}

      <div ref={panelRef} className={`cc-panel${mode === 'roi' ? ' cc-panel--no-bottom-radius' : ''}`}>
        {mode === 'full' && <FullTable data={data} />}
        {mode === 'roi' && <ROITable data={data} />}
        {mode === 'compact' && <CompactTable data={data} />}
        {mode === 'features' && <FeatureTable data={data} />}
      </div>

      {mode === 'roi' && (
        <div className="cc-savings-bar">
          <span>Mascotbot Starter vs HeyGen, per minute</span>
          <span className="cc-savings-badge">Up to 80% less</span>
        </div>
      )}

      <p className="cc-disclaimer">
        Pricing as of {asOf}. Sourced from official websites and public documentation.
        Mascotbot&apos;s per-minute figure is the usage-based Starter rate; its production plans bill a flat price per monthly active user. Enterprise pricing varies.
      </p>
    </div>
  );
}
